// BuyFiat.tsx — FINAL VERSION
// Fixes iOS portrait blank issue (overflow reset) + Stripe button fully visible

import React, { useEffect, useState } from "react";
import { BuyWidget, darkTheme } from "thirdweb/react";
import { createThirdwebClient } from "thirdweb";
import { bsc } from "thirdweb/chains";

// ✅ Thirdweb client setup
const client = createThirdwebClient({
  clientId: "6e9d898f6bae315a1b8ea5120b9ac6b5",
});

// ✅ Hook: Reset scroll lock (fixes iOS Safari portrait disappearance)
function useResetOverflow() {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "visible";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);
}

export default function BuyFiat() {
  useResetOverflow();

  const [mounted, setMounted] = useState(false);
  const [stripeUrl, setStripeUrl] = useState<string | null>(null);

  // Load Stripe Onramp redirect URL
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.StripeOnramp && !stripeUrl) {
        try {
          const standaloneOnramp = window.StripeOnramp.Standalone({
            source_currency: "usd",
            amount: { source_amount: "100" },
            destination_networks: ["ethereum", "polygon", "solana", "bitcoin"],
            destination_currencies: ["eth", "usdc", "sol", "btc"],
            destination_network: "ethereum",
            destination_currency: "eth",
          });
          const url = standaloneOnramp.getUrl();
          setStripeUrl(url);
          clearInterval(interval);
        } catch (err) {
          console.error("Stripe Onramp init error:", err);
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [stripeUrl]);

  // Mount delay to prevent SSR/React hydration flicker
  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 400);
    return () => clearTimeout(timer);
  }, []);

  if (!mounted) return null;

  return (
    <div
      style={{
        width: "100vw",
        minHeight: "100vh",
        background: "#0b0b14",
        color: "#fff",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "visible",
        position: "relative",
      }}
    >
      {/* --- Title Section --- */}
      <div style={{ marginTop: "24px", textAlign: "center", marginBottom: "12px" }}>
        <h1 style={{ fontSize: "1.8rem", fontWeight: 700, marginBottom: "4px" }}>
          Buy Crypto with Fiat
        </h1>
        <p style={{ fontSize: "0.95rem", color: "#ccc" }}>
          Instantly buy crypto using card, Apple&nbsp;Pay, or bank transfer turn to landscape when choosing chain & token.
        </p>
      </div>

      {/* --- Thirdweb Buy Widget --- */}
      <div
        style={{
          width: "100%",
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BuyWidget
          client={client}
          chain={bsc}
          showThirdwebBranding={false}
          theme={darkTheme({
            colors: {
              modalBg: "#0b0b14",
              accentButtonBg: "hsl(221 83% 54%)",
              accentButtonText: "#ffffff",
              primaryText: "#ffffff",
              secondaryText: "#cccccc",
              borderColor: "#333",
            },
          })}
          style={{
            width: "100%",
            height: "100%",
            background: "#0b0b14",
          }}
        />
      </div>

      {/* --- Stripe Hosted Onramp Fallback (Always visible button) --- */}
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          left: 0,
          width: "100%",
          textAlign: "center",
          fontSize: "0.95rem",
          opacity: 0.95,
          zIndex: 9999,
          background: "rgba(11,11,20,0.9)",
          padding: "0.9rem 0.5rem",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.3)",
          backdropFilter: "blur(6px)",
        }}
      >
        Having trouble?{" "}
        {stripeUrl ? (
          <a
            href={stripeUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#4e8cff",
              textDecoration: "none",
              fontWeight: 700,
              fontSize: "1rem",
              marginLeft: "0.25rem",
            }}
          >
            Try Stripe Onramp
          </a>
        ) : (
          <span style={{ opacity: 0.6 }}>Loading Stripe link…</span>
        )}
      </div>
    </div>
  );
}
